import React, { useState } from "react";
import { Package, Users, DollarSign, TrendingUp } from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import "./AnalyticsPage.css";

const monthlyData = [
  { month: "Jan", shipments: 1234, couriers: 45, revenue: 41200, completion: 95.2 },
  { month: "Feb", shipments: 1456, couriers: 52, revenue: 48600, completion: 96.1 },
  { month: "Mar", shipments: 1398, couriers: 58, revenue: 46800, completion: 94.8 },
  { month: "Apr", shipments: 1567, couriers: 61, revenue: 52300, completion: 97.3 },
  { month: "May", shipments: 1689, couriers: 67, revenue: 56400, completion: 96.9 },
  { month: "Jun", shipments: 1304, couriers: 54, revenue: 44800, completion: 95.5 },
];

export default function AnalyticsPage() {
  const [activeView, setActiveView] = useState("packages");

  const viewOptions = [
    { id: "packages", label: "Packages", icon: Package, color: "#2D58AD", bgClass: "tab-bg-blue" },
    { id: "couriers", label: "Couriers", icon: Users, color: "#71A4F4", bgClass: "tab-bg-light-blue" },
    { id: "revenue", label: "Revenue", icon: DollarSign, color: "#22c55e", bgClass: "tab-bg-green" },
    { id: "performance", label: "Performance", icon: TrendingUp, color: "#eab308", bgClass: "tab-bg-yellow" },
  ];

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h1 className="analytics-title">Analytics</h1>
        <p className="analytics-subtitle">
          Comprehensive performance insights
        </p>
      </div>

      <div className="analytics-tabs-container">
        {viewOptions.map((option) => (
          <button
            key={option.id}
            onClick={() => setActiveView(option.id)}
            className={`analytics-tab ${activeView === option.id ? "active" : ""}`}
          >
            <div className="analytics-tab-content">
              <div className={`analytics-tab-icon-wrapper ${option.bgClass}`}>
                <option.icon className="analytics-tab-icon" style={{ color: option.color }} size={24} />
              </div>
              <span
                className={`analytics-tab-label ${
                  activeView === option.id ? "active-label" : ""
                }`}
              >
                {option.label}
              </span>
            </div>
          </button>
        ))}
      </div>

      <div className="analytics-view-container fade-in" key={activeView}>
        {activeView === "packages" && (
          <div className="analytics-card main-chart-card">
            <h2 className="analytics-card-title">
              Shipment Trends - Monthly Overview
            </h2>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={monthlyData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={true} />
                  <XAxis dataKey="month" stroke="#708094" axisLine={true} tickLine={true} dy={10} />
                  <YAxis
                    stroke="#708094"
                    axisLine={false}
                    tickLine={false}
                    ticks={[0, 450, 900, 1350, 1800]}
                    domain={[0, 1800]}
                    dx={-10}
                  />
                  <Line
                    type="monotone"
                    dataKey="shipments"
                    stroke="#2D58AD"
                    strokeWidth={3}
                    dot={{ fill: "#2D58AD", r: 6, strokeWidth: 0 }}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="analytics-summary-grid">
              <div className="summary-box summary-box-blue-tint">
                <p className="summary-label-sm">Total Shipments</p>
                <p className="summary-value-lg text-blue">8,648</p>
              </div>
              <div className="summary-box summary-box-green-tint">
                <p className="summary-label-sm">Delivered</p>
                <p className="summary-value-lg text-green">8,199</p>
              </div>
              <div className="summary-box summary-box-red-tint">
                <p className="summary-label-sm">Cancelled</p>
                <p className="summary-value-lg text-red">449</p>
              </div>
            </div>
          </div>
        )}

        {activeView === "couriers" && (
          <div className="analytics-card main-chart-card">
            <h2 className="analytics-card-title">Courier Performance Metrics</h2>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={monthlyData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={true} />
                  <XAxis dataKey="month" stroke="#708094" axisLine={true} tickLine={true} dy={10} />
                  <YAxis stroke="#708094" axisLine={false} tickLine={false} dx={-10} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar dataKey="couriers" fill="#71A4F4" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="analytics-summary-grid-2">
              <div className="summary-box summary-box-green-tint">
                <p className="summary-label-sm">Active Couriers</p>
                <p className="summary-value-lg text-green">87</p>
              </div>
              <div className="summary-box summary-box-yellow-tint">
                <p className="summary-label-sm">On Break</p>
                <p className="summary-value-lg text-yellow">12</p>
              </div>
            </div>
          </div>
        )}

        {activeView === "revenue" && (
          <div className="analytics-card main-chart-card">
            <h2 className="analytics-card-title">Revenue Analysis - Monthly Trend</h2>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={monthlyData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={true} />
                  <XAxis dataKey="month" stroke="#708094" axisLine={true} tickLine={true} dy={10} />
                  <YAxis stroke="#708094" axisLine={false} tickLine={false} dx={-10} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#2D58AD"
                    strokeWidth={3}
                    dot={{ fill: "#2D58AD", r: 6, strokeWidth: 0 }}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="analytics-summary-grid">
              <div className="summary-box summary-box-blue-tint">
                <p className="summary-label-sm">Total Revenue</p>
                <p className="summary-value-lg text-blue">₼290.1K</p>
              </div>
              <div className="summary-box summary-box-green-tint">
                <p className="summary-label-sm">Avg per Shipment</p>
                <p className="summary-value-lg text-green">₼33.55</p>
              </div>
              <div className="summary-box summary-box-purple-tint">
                <p className="summary-label-sm">Growth Rate</p>
                <p className="summary-value-lg text-purple">+18%</p>
              </div>
            </div>
          </div>
        )}

        {activeView === "performance" && (
          <div className="analytics-card main-chart-card">
            <h2 className="analytics-card-title">Completion & Success Rates</h2>
            <div className="completion-grid">
              <div className="completion-col">
                <h3 className="completion-col-title">Delivery Success Rate</h3>
                <div className="completion-bars-container">
                  <div className="completion-bar-item">
                    <div className="completion-bar-header">
                      <span className="completion-bar-label">On-Time Deliveries</span>
                      <span className="completion-bar-value text-green">94.8%</span>
                    </div>
                    <div className="completion-bar-bg">
                      <div className="completion-bar-fill bg-green" style={{ width: "94.8%" }}></div>
                    </div>
                  </div>
                  <div className="completion-bar-item">
                    <div className="completion-bar-header">
                      <span className="completion-bar-label">Delayed Deliveries</span>
                      <span className="completion-bar-value text-yellow">3.8%</span>
                    </div>
                    <div className="completion-bar-bg">
                      <div className="completion-bar-fill bg-yellow" style={{ width: "3.8%" }}></div>
                    </div>
                  </div>
                  <div className="completion-bar-item">
                    <div className="completion-bar-header">
                      <span className="completion-bar-label">Failed Deliveries</span>
                      <span className="completion-bar-value text-red">1.4%</span>
                    </div>
                    <div className="completion-bar-bg">
                      <div className="completion-bar-fill bg-red" style={{ width: "1.4%" }}></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="completion-col">
                <h3 className="completion-col-title">Performance Metrics</h3>
                <div className="completion-metrics-container">
                  <div className="summary-box summary-box-blue-tint">
                    <p className="summary-label-sm">Average Delivery Time</p>
                    <p className="summary-value-lg text-blue">2.4 days</p>
                  </div>
                  <div className="summary-box summary-box-green-tint">
                    <p className="summary-label-sm">Customer Satisfaction</p>
                    <p className="summary-value-lg text-green">92.8%</p>
                  </div>
                  <div className="summary-box summary-box-purple-tint">
                    <p className="summary-label-sm">Route Efficiency</p>
                    <p className="summary-value-lg text-purple">88.5%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
